import React, { useState, useMemo, useEffect, useCallback } from "react";
// import PropTypes from "prop-types";
import axios from "axios";
import { DataTable } from "components/DataTable/DataTable";
import { DataViewContainer } from "components/DataViewContainer/DataViewContainer";
import SelectColumnFilter from "components/DataTable/SelectColumnFilter";
import DateFilter from "components/DataTable/DateFilter";
import moment from "moment";

const OrderDetail = (props) => {
  const [data, setData] = useState([]);
  const [yearData, setYearData] = useState([]);
  const [enableCSV, setEnableCSV] = useState(0);
  const [loading, setLoading] = useState(true);
  const { currentUser } = props;
  const currentYear = moment().format("YYYY");
  const currentMonth = moment().format("M");

  const adminColumns = useMemo(() => [
    {
      Header: "Sample ID",
      accessor: "SampleID",
    },
    {
      Header: "Accession",
      accessor: "Accession",
    },
    {
      Header: "Test Type",
      accessor: "TestType",
    },
    {
      Header: "Type",
      accessor: "Type",
    },
    {
      Header: "Order ID",
      accessor: "OrderID",
    },
    {
      Header: "Patient Name",
      accessor: "PatientName",
    },
    {
      Header: "DOB",
      accessor: "DOB",
    },
    {
      Header: "Account",
      accessor: "Account",
    },
    {
      Header: "Account Name",
      accessor: "AccountName",
    },
    {
      Header: "Provider",
      accessor: "Provider",
    },
    {
      Header: "DOS",
      accessor: "DOS",
      Filter: DateFilter,
      filter: "dateBetween",
    },
    {
      Header: "DOR",
      accessor: "DOR",
      Filter: DateFilter,
      filter: "dateBetween",
    },
    {
      Header: "Status",
      accessor: "Status",
      Filter: SelectColumnFilter,
      filter: "includes",
    },
    {
      Header: "Approved Date",
      accessor: "ApprovedDate",
    },
    {
      Header: "Primary Payer",
      accessor: "PrimaryPayer",
    },
    {
      Header: "Hold Reason",
      accessor: "HoldReason",
    },
    {
      Header: "Reject Reason",
      accessor: "RejectReason",
    },
    {
      Header: "Sales Rep",
      accessor: "SalesRep",
    },
    {
      Header: "Supervising Rep",
      accessor: "SUPERREP",
    },
  ]);

  const repColumns = useMemo(() => [
    {
      Header: "Sample ID",
      accessor: "SampleID",
    },
    {
      Header: "Accession",
      accessor: "Accession",
    },
    {
      Header: "Order ID",
      accessor: "OrderID",
    },
    {
      Header: "Patient Name",
      accessor: "PatientName",
    },
    {
      Header: "DOB",
      accessor: "DOB",
    },
    {
      Header: "Account",
      accessor: "Account",
    },
    {
      Header: "Account Name",
      accessor: "AccountName",
    },
    {
      Header: "Provider",
      accessor: "Provider",
    },
    {
      Header: "Test Type",
      accessor: "TestType",
    },
    {
      Header: "DOS",
      accessor: "DOS",
      Filter: DateFilter,
      filter: "dateBetween",
    },
    {
      Header: "DOR",
      accessor: "DOR",
      Filter: DateFilter,
      filter: "dateBetween",
    },
    {
      Header: "Status",
      accessor: "Status",
      Filter: SelectColumnFilter,
      filter: "includes",
    },
    {
      Header: "Approved Date",
      accessor: "ApprovedDate",
    },
    {
      Header: "Primary Payer",
      accessor: "PrimaryPayer",
    },
    {
      Header: "Hold Reason",
      accessor: "HoldReason",
    },
    {
      Header: "Reject Reason",
      accessor: "RejectReason",
    },
    {
      Header: "Type",
      accessor: "Type",
    },
    {
      Header: "Rep",
      accessor: "RepName",
    },
    {
      Header: "Filing Indicator",
      accessor: "FilingIndicator",
    },
  ]);

  // Function to grab different columns for rep and admin views
  const getColumnType = (user) => {
    if (user.role === "Admin") {
      return adminColumns;
    } else if (user.email && user.role === "Rep") {
      return repColumns;
    } else {
      return null;
    }
  };

  const fetchData = useCallback(async (user, cy, cm) => {
    let url;
    let result;
    if (user.role === "Admin") {
      url = `${process.env.REACT_APP_REST_API_HOST}getadminordersmonthly?year=${cy}&month=${cm}&apikey=${process.env.REACT_APP_REST_API_KEY}`;
      result = await axios.get(url);
      setLoading(false);
      setData(result.data);
    } else if (user.email && user.role === "Rep") {
      url = `${process.env.REACT_APP_REST_API_HOST}getrepordersmonthly?user_email=${user.email}&year=${cy}&month=${cm}&apikey=${process.env.REACT_APP_REST_API_KEY}`;
      result = await axios.get(url);
      setLoading(false);
      setData(result.data);
    } else {
      setLoading(false);
      setData([]);
    }
  }, []);

  // Function to query data based off of rep and admin roles
  const fetchYearData = useCallback(async (user, cy) => {
    let url;
    let result;
    if (user.email && user.role === "Rep") {
      setEnableCSV(1);
      url = `${process.env.REACT_APP_REST_API_HOST}getrepordersmonthly?user_email=${user.email}&year=${cy}&apikey=${process.env.REACT_APP_REST_API_KEY}`;
      result = await axios.get(url);
      setEnableCSV(2);
      setYearData(result.data);
    } else if (user.role === "Admin") {
      setEnableCSV(1);
      url = `${process.env.REACT_APP_REST_API_HOST}getadminordersmonthly?year=${cy}&apikey=${process.env.REACT_APP_REST_API_KEY}`;
      result = await axios.get(url);
      setEnableCSV(2);
      setYearData(result.data);
    } else {
      setEnableCSV(2);
      setYearData([]);
    }
  }, []);

  useEffect(() => {
    fetchData(currentUser, currentYear, currentMonth);
  }, [fetchData, currentUser, currentMonth, currentYear]);

  return (
    <DataViewContainer
      pageTitle="Order Detail Data"
      loading={loading}
      fetchData={fetchData}
      currentUser={currentUser}
      setLoading={setLoading}
    >
      {!loading ? (
        <>
          <DataTable
            data={data}
            columns={getColumnType(currentUser)}
            yearData={yearData}
            enableCSV={enableCSV}
            fetchYearData={fetchYearData}
            currentUser={currentUser}
          />
        </>
      ) : undefined}
    </DataViewContainer>
  );
};

export default OrderDetail;
